<template>
  <div id="create-accounts-component">
    <div v-if="errorUsers.length > 0" class="k-create-accounts-alert alert alert-danger">
      <span><b>Error: </b> Unable to create some accounts</span>
      <div class="alert-actions">
        <button id="create-accounts-export-errors-button" class="btn btn-primary" @click="exportFailedUsers">
          Export Failed Users
          <i class="fas fa-file-download" aria-hidden="true"></i>
        </button>
        <button id="create-accounts-retry-button" v-if="!hasRetried" class="btn btn-success" @click="retryFailedUsers">
          Retry
          <i class="fas fa-undo-alt" aria-hidden="true"></i>
        </button>
      </div>
    </div>
    <div class="new-users-table-container">
      <k-table :hideable="false" v-if="!ready" :panel="false" :rows="users" :headers="kableHeaders" :max="30"></k-table>
      <k-table v-else :key="2" :hideable="false" :panel="false" :rows="results" :max="30" :headers="kableRegisteredHeaders"></k-table>
    </div>
    <div class="create-acc-footer">
      <button v-if="!ready" class="btn btn-success" :disabled="loading" @click="registerUsers">
        {{ loading ? 'Creating Accounts...' : 'Create Accounts' }}
        <i v-if="!loading" class="fa-solid fa-plus" aria-hidden="true"></i>
      </button>
      <div v-if="ready && successUsers.length > 0" class="k-create-account-right">
        <button class="btn btn-primary" @click="finish">
          Finish <i class="fas fa-check" aria-hidden="true"></i>
        </button>
      </div>
      <k-loader v-if="loading"></k-loader>
    </div>
  </div>
</template>

<style scoped>
.create-acc-footer {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  padding-top: 10px;
  margin-top: 20px;
  border-top: 2px solid var(--kate-background-body-alpha);
}

.create-acc-footer button {
  opacity: 1;
}

.k-create-accounts-alert {
  text-align: center;
  margin: 30px 0;
  max-width: 500px;
}

.k-table-key-success-true {
  color: var(--kate-success);
}

.k-table-key-success-false {
  color: var(--kate-danger);
}

.alert-actions {
  margin-top: 15px;
  display: flex;
  gap: 15px;
  justify-content: space-between;
}
</style>

<script>
import CSVMixin from '@mixins/csv-mixins';
import KTable from '@base-components/k-table.vue';
import KLoader from '@base-components/k-loader.vue';

const MAX_PAYLOAD_SIZE = 30;

export default {
  mixins: [CSVMixin],
  components: {
    'k-table': KTable,
    KLoader,
  },
  props: {
    users: {
      type: Array,
      required: true,
      notes: 'user email format, e.g. [{ name: somename, email: some@mail.com, line_manager_full_name: some_line_manager_name, line_manager_email: some_line_manager_email }]',
    },
    programme: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.refinedUsers = this.processUsers();
  },
  data() {
    return {
      userStatus: [],
      refinedUsers: [],
      hasRetried: false,
      ready: false,
      MAX_PAYLOAD_SIZE,
      loading: false,
      results: [],
    };
  },
  computed: {
    successUsers() {
      if (!this.ready) {
        return [];
      }
      const output = [];
      for (let i = 0; i < this.results.length; i++) {
        if (this.results[i].success) {
          output.push(this.results[i]);
        }
      }
      return output;
    },
    errorUsers() {
      if (!this.ready) {
        return [];
      }
      const output = [];
      for (let i = 0; i < this.results.length; i++) {
        if (!this.results[i].success) {
          output.push(this.results[i]);
        }
      }
      return output;
    },
    kableRegisteredHeaders() {
      const headers = {
        name: {
          name: 'Name',
          filterable: true,
        },
        email: {
          name: 'Email',
          filterable: true,
        },
        lineManagerName: {
          name: 'Line Manager Name',
          filterable: true,
        },
        lineManagerEmail: {
          name: 'Line Manager Email',
          filterable: true,
        },
        groupTags: {
          name: 'Groups',
          filterable: true,
          type: 'tags',
        },
        domainName: {
          name: 'Domain',
          filterable: true,
        },
        employerName: {
          name: 'Company',
          filterable: true,
        },
        success: {
          name: 'Status',
          type: 'boolean',
          sortable: true,
          options: {
            true: '<i class="fas fa-check-circle" aria-hidden="true"></i><span class="toast-text">Account Created</span>',
            false: '<i class="fas fa-exclamation-triangle" aria-hidden="true"></i><span class="toast-text">Error</span>',
          },
        },
      };
      if (!this.programmeHasGroups) {
        delete headers.groupTags;
      }
      return headers;
    },
    kableDownloadHeaders() {
      return {
        creation_status: {
          name: 'creation_status',
        },
        error: {
          name: 'error',
        },
        error_code: {
          name: 'error_code',
        },
        programme_id: {
          name: 'programme_id',
        },
        ...this.kableRegisteredHeaders,
      };
    },
    kableHeaders() {
      const headers = {
        name: {
          name: 'Name',
          filterable: true,
        },
        email: {
          name: 'Email',
          filterable: true,
        },
        lineManagerName: {
          name: 'Line Manager Name',
          filterable: true,
        },
        lineManagerEmail: {
          name: 'Line Manager Email',
          filterable: true,
        },
        groupTags: {
          name: 'Groups',
          filterable: true,
          type: 'tags',
        },
        domainName: {
          name: 'Domain',
          filterable: true,
        },
        employerName: {
          name: 'Company',
          filterable: true,
        },
      };
      if (!this.programmeHasGroups) {
        delete headers.groupTags;
      }
      return headers;
    },
    programmeHasGroups() {
      return Boolean(this.programme.programme_groups && this.programme.programme_groups.length > 0);
    },
  },
  methods: {
    processResults(results) {
      let tempId;
      let entry;
      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i].data.length; j++) {
          tempId = results[i].data[j].temp_id;
          entry = results[i].data[j];
          entry.success = entry.creation_status >= 200 && entry.creation_status < 300;
          this.results.push({ ...this.refinedUsers[tempId], ...entry });
        }
      }
      this.$logger.info('Successfully added users to programme', { programmeId: this.programme.id, users: this.refinedUsers }, true);
    },
    processUsers() {
      return this.users.map((val, index) => ({
        temp_id: index,
        full_name: val.name,
        ...val,
      }));
    },
    partitionUsers(users) {
      const output = [];
      for (let i = 0, j = users.length; i < j; i += this.MAX_PAYLOAD_SIZE) {
        output.push(users.slice(i, i + this.MAX_PAYLOAD_SIZE));
      }
      return output;
    },
    registerUserCall(users) {
      const userPayload = users.map(x => {
        const user = {
          temp_id: x.temp_id, // Need this to match the results back to the users sent in the payload
          email: x.email,
          full_name: x.name,
          programmes: [{
            id: this.programme.id,
            groups: x.groups,
          }],
          line_manager_full_name: x.lineManagerName || null,
          line_manager_email: x.lineManagerEmail || null,
        };
        if (x.employer_id) {
          user.employer_id = x.employer_id;
        }
        if (x.domain) {
          user.domain = x.domain;
        }
        return user;
      });
      return this.$http.post('/api/profile/user/bulk', {
        users: userPayload,
      });
    },
    rejectedBatchResponse(users, err) {
      this.$logger.error('Error adding a batch of users to programme', { programmeId: this.programme.id, users }, err);
      return {
        // Stand in for the response the service never sent, so processResults can mark the batch failed
        data: users.map(x => ({
          temp_id: x.temp_id,
          creation_status: err.response ? err.response.status : 0,
          error: err.message,
        })),
      };
    },
    registerCall(chunks) {
      this.ready = false;
      this.loading = true;
      // Sent one at a time so a large upload never holds more than one bulk request in flight
      return chunks.reduce(
        (chain, chunk) => chain.then(responses => this.registerUserCall(chunk)
          .catch(err => this.rejectedBatchResponse(chunk, err))
          .then(response => [...responses, response])),
        Promise.resolve([]),
      ).then(responses => {
        this.processResults(responses);
        this.ready = true;
        this.loading = false;
      });
    },
    exportFailedUsers() {
      this.downloadCsvFromTable(this.kableDownloadHeaders, this.errorUsers, 'error_users.csv');
    },
    retryFailedUsers() {
      const chunks = this.partitionUsers(this.errorUsers);
      this.registerCall(chunks);
      this.hasRetried = true;
    },
    registerUsers() {
      this.$logger.info('Adding users to programme', { programmeId: this.programme.id, users: this.refinedUsers }, true);
      const chunks = this.partitionUsers(this.refinedUsers);
      this.registerCall(chunks);
    },
    finish() {
      this.$emit('finish', this.successUsers);
    },
  },
};
</script>
